/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MdCheck } from 'react-icons/md';
import {
  addYears,
  endOfDay,
  format,
  parseISO,
  startOfDay,
  subMonths,
} from 'date-fns';
import { TableList, Container, Filter, Status } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import { IOption } from '~/components/Select';
import Loading from '~/components/Loading';
import QtyRealized from './QtyRealized';
import Sold from './Sold';

interface IDataItem {
  id: number;
  organizer: string;
  event: string;
  date: string;
  location: string;
  status: string;
  expected_qty: number;
  realized_qty: number;
  total: string;
  received: string;
  to_receive: string;
  active: boolean;
}

interface IItem {
  id: number;
}

interface IOrder {
  processing_fee: string;
  service_fee: string;
  total: string;
  items: IItem[];
}

interface IBatchCategory {
  quantity: number;
  price: string;
}

interface IBatch {
  id: number;
  quantity: number;
  due_date: string;
  batchCategories: IBatchCategory[];
}

interface IArchiveEvent {
  archive: {
    archive_url: string;
  };
}

interface IEventData {
  id: number;
  name: string;
  description: string;
  date: string;
  slug: string;
  archivesEvents: IArchiveEvent[];
  batches: IBatch[];
  orders: IOrder[];
  address: {
    street: string;
    number: string;
    neighborhood: string;
    city: string;
    state: string;
    country: string;
    complement: string;
    zip_code: string;
  };
  organizer: {
    name: string;
  };
  status: {
    name: string;
  };
}

interface IEventResponse {
  data: IEventData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IStatus {
  id: number;
  name: string;
}

interface IOrganizer {
  id: number;
  name: string;
}

const Events: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [eventName, setEventName] = useState<string | undefined>(undefined);
  const [statusId, setStatusId] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<IOption[]>([]);
  const [quantity, setQuantity] = useState(10);
  const [loading, setLoading] = useState(false);
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [organizerId, setOrganizerId] = useState<string | undefined>(undefined);

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const dataStatus = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(dataStatus);
    });

    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const handleLoadEvents = useCallback(
    async (
      pageData,
      name?: string,
      status_id?: string,
      dateStart?: string,
      dateEnd?: string,
      quantityData?: number,
      organizer_id?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IEventResponse>('events/admin', {
          params: {
            page: pageData,
            name,
            status_ids: status_id ? [status_id] : undefined,
            startDate: dateStart ? new Date(dateStart) : undefined,
            endDate: dateEnd ? new Date(dateEnd) : undefined,
            isList: true,
            quantity: quantityData,
            organizer_id,
          },
        })
        .finally(() => {
          setLoading(false);
        });
      const dataAux = response.data.data.map<IDataItem>((event) => {
        const allTickets = event.batches.reduce((previous, current) => {
          const totalTickets = current.batchCategories.reduce(
            (subPrevious, subCurrent) => subPrevious + subCurrent.quantity,
            0
          );
          return previous + totalTickets;
        }, 0);

        const purchases = event.orders.reduce(
          (previous, current) => previous + current.items.length,
          0
        );

        const location = `${event.address.street} n˚ ${event.address.number}, ${event.address.neighborhood} - ${event.address.city}/${event.address.state}, CEP: ${event.address.zip_code}`;

        const total = event.batches.reduce((previous, current) => {
          const totalTickets = current.batchCategories.reduce(
            (subPrevious, subCurrent) =>
              subPrevious + subCurrent.quantity * parseFloat(subCurrent.price),
            0
          );
          return previous + totalTickets;
        }, 0);

        const received = event.orders.reduce((previous, current) => {
          return (
            previous +
            (parseFloat(current.total) -
              parseFloat(current.processing_fee) -
              parseFloat(current.service_fee))
          );
        }, 0);

        const to_receive = received - received * 0.03;
        const koro = received * 0.03;

        return {
          id: event.id,
          organizer: event.organizer.name,
          event: event.name,
          date: format(parseISO(event.date), 'dd/MM/yyyy'),
          location,
          status: event.status ? event.status.name : 'Cadastrando',
          expected_qty: allTickets,
          realized_qty: purchases,
          total: formatPrice(total),
          received: formatPrice(received),
          to_receive: formatPrice(to_receive),
          koro: formatPrice(koro),
          active: true,
        };
      });

      setData(dataAux);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    []
  );

  useEffect(() => {
    handleLoadEvents(
      1,
      undefined,
      undefined,
      startOfDay(subMonths(new Date(), 6)).toISOString(),
      endOfDay(addYears(new Date(), 1)).toISOString()
    );
  }, [handleLoadEvents]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: '#',
        selector: 'id',
        sortable: false,
      },
      {
        name: 'Evento',
        selector: 'event',
        sortable: true,
      },
      {
        name: 'Organizador',
        selector: 'organizer',
        sortable: true,
      },
      {
        name: 'Data',
        selector: 'date',
        sortable: true,
      },
      {
        name: 'Local',
        selector: 'location',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        sortable: true,
        cell: (row) => (
          <Status status={row.status} className="text-white rounded-pill mb-0">
            {row.status === 'Esgotado' ? 'Fim vendas' : row.status}
          </Status>
        ),
      },
      {
        name: 'Qtd prevista',
        selector: 'expected_qty',
        sortable: true,
      },
      {
        name: 'Qtd realizada',
        selector: 'realized_qty',
        sortable: true,
        cell: (row) => <QtyRealized eventId={row.id} />,
      },
      {
        name: 'Meta',
        selector: 'total',
        sortable: true,
      },
      {
        name: 'Vendido',
        selector: 'received',
        sortable: true,
        cell: (row) => <Sold eventId={row.id} />,
      },
      // {
      //   name: 'A receber',
      //   selector: 'to_receive',
      //   sortable: true,
      // },
      {
        name: 'Ativo',
        selector: 'active',
        sortable: true,
        cell: () => (
          <button type="button" className="pe-none btn-icons border-0">
            <MdCheck size={16} color="#fff" />
          </button>
        ),
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadEvents(
          page,
          eventName,
          statusId,
          startDate,
          endDate,
          quantity,
          organizerId
        );
        setPageSelected(page);
      }
    },
    [
      endDate,
      eventName,
      handleLoadEvents,
      startDate,
      statusId,
      tableData.totalPage,
      quantity,
      organizerId,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setEventName(formData.event === 'Todos' ? undefined : formData.event);
      setStatusId(formData.status !== 'todos' ? formData.status : undefined);
      setStartDate(formData.dateStart);
      setEndDate(formData.dateEnd);
      setOrganizerId(
        formData.organizer !== 'todos' ? formData.organizer : undefined
      );
      handleLoadEvents(
        1,
        formData.event === 'Todos' ? undefined : formData.event,
        formData.status !== 'todos' ? formData.status : undefined,
        formData.dateStart,
        formData.dateEnd,
        quantity,
        formData.organizer !== 'todos' ? formData.organizer : undefined
      );
      setPageSelected(1);
    },
    [handleLoadEvents, quantity]
  );

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      handleLoadEvents(
        1,
        eventName,
        statusId,
        startDate,
        endDate,
        quantityData,
        organizerId
      );
      setQuantity(quantityData);
      setPageSelected(1);
    },
    [endDate, eventName, handleLoadEvents, startDate, statusId, organizerId]
  );

  const handleClickExportCsv = useCallback(async () => {
    try {
      setLoading(true);

      const { data: events } = await api.get<IEventData[]>('events/admin', {
        params: {
          name: eventName,
          status_ids: statusId,
          startDate,
          endDate,
          isList: true,
          organizer_id: organizerId,
        },
      });

      const dataAux = await Promise.all(
        events.map(async (event) => {
          const { data: orders } = await api.get<IOrder[]>(
            `orders/events/${event.id}`
          );

          const realizedQty = orders.reduce(
            (prev, order) => prev + order.items.length,
            0
          );

          const allTickets = event.batches.reduce((previous, current) => {
            const totalTickets = current.batchCategories.reduce(
              (subPrevious, subCurrent) => subPrevious + subCurrent.quantity,
              0
            );
            return previous + totalTickets;
          }, 0);

          const total = event.batches.reduce((previous, current) => {
            const totalTickets = current.batchCategories.reduce(
              (subPrevious, subCurrent) =>
                subPrevious +
                subCurrent.quantity * parseFloat(subCurrent.price),
              0
            );
            return previous + totalTickets;
          }, 0);

          const location = `${event.address.street} n˚ ${event.address.number}, ${event.address.neighborhood} - ${event.address.city}/${event.address.state}, CEP: ${event.address.zip_code}`;

          const received = orders.reduce((previous, current) => {
            return (
              previous +
              (parseFloat(current.total) -
                parseFloat(current.processing_fee) -
                parseFloat(current.service_fee))
            );
          }, 0);

          const to_receive = received - received * 0.03;

          return {
            id: event.id,
            organizer: event.organizer.name,
            event: event.name,
            date: format(parseISO(event.date), 'dd/MM/yyyy'),
            location,
            status: event.status
              ? `${
                  event.status.name === 'Esgotado'
                    ? 'Fim vendas'
                    : event.status.name
                }`
              : 'Cadastrando',
            expected_qty: allTickets,
            realized_qty: realizedQty,
            total: total.toFixed(2).replace('.', ','),
            received: received.toFixed(2).replace('.', ','),
            to_receive: to_receive.toFixed(2).replace('.', ','),
            active: true,
          };
        })
      );

      return dataAux;
    } catch (error) {
      return [];
      console.error('Erro ao exportar CSV', error);
    } finally {
      setLoading(false);
    }
  }, [endDate, eventName, organizerId, startDate, statusId]);

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de eventos</h1>
              <ExportCsv
                data={data}
                columns={columns}
                fileName="Lista_de_eventos"
                onClick={handleClickExportCsv}
              />
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              event
              date
              statusEvent
              statusOptions={status}
              organizer
              organizersData={organizers}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        changeQuantity
                        showTotals
                        generalTotal={tableData.generalTotal}
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        onChangeQuantity={handleChangeQuantity}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Events;
